import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistDialog({ city, open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setName(""); setEmail(""); setPhone(""); setDone(false); setBusy(false);
  };

  const handleClose = (v) => {
    if (!v) { reset(); onClose(); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email && !phone) {
      toast.error("Add an email or phone to join the waitlist");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/waitlist`, {
        city_slug: city.slug, email: email || null, phone: phone || null, name: name || null,
      });
      setDone(true);
      toast.success(`You're on the ${city.name} waitlist!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (!city) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="waitlist-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">
            Join the <span style={{ color: "var(--secondary)" }}>{city.name}</span> waitlist
          </DialogTitle>
          <DialogDescription>
            {city.name} opens on <b>{city.launch}</b>. Drop your contact and we'll ping you the moment it goes live.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: "var(--secondary)" }} />
            <div className="font-display text-2xl mt-3">You're in.</div>
            <p className="text-sm text-neutral-600 mt-2">We'll notify you when {city.name} goes live.</p>
            <button className="btn-primary mt-6" onClick={() => handleClose(false)}>Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-500">Name (optional)</Label>
              <Input data-testid="waitlist-name" className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-500">Email</Label>
              <Input data-testid="waitlist-email" type="email" className="mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-500">Phone (with country code)</Label>
              <Input data-testid="waitlist-phone" className="mt-1.5" placeholder="+91…" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <button data-testid="waitlist-submit" className="btn-primary w-full" disabled={busy}>
              {busy ? "Adding…" : `Notify me when ${city.name} launches`}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
